// src/pages/admin/ManageSlots.js
import React, { useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";

function ManageSlots() {
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState("");
  const [venueSports, setVenueSports] = useState([]);
  const [courtName, setCourtName] = useState("");
  const [selectedSports, setSelectedSports] = useState([]);
  const [slots, setSlots] = useState([{ startTime: "", endTime: "", price: "" }]);
  const [defaultPrice, setDefaultPrice] = useState("");
  const [courts, setCourts] = useState([]);
  const [editCourtId, setEditCourtId] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const priceToNumber = (p) =>
    p === "" || p == null ? 0 : Number(String(p).replace(/[^\d]/g, ""));

  const sanitizeSlots = (list) =>
    list
      .map((s) => ({
        startTime: s.startTime?.trim() || "",
        endTime: s.endTime?.trim() || "",
        price: priceToNumber(s.price),
      }))
      .filter((s) => s.startTime && s.endTime);

  const timesToMinutes = (t) => {
    const [h, m] = (t || "00:00").split(":").map(Number);
    return h * 60 + m;
  };

  const validateNoOverlap = (list) => {
    const intervals = list.map((s) => ({
      start: timesToMinutes(s.startTime),
      end: timesToMinutes(s.endTime),
    }));
    for (const it of intervals) {
      if (it.end <= it.start) return false;
    }
    intervals.sort((a, b) => a.start - b.start);
    for (let i = 1; i < intervals.length; i++) {
      if (intervals[i].start < intervals[i - 1].end) return false;
    }
    return true;
  };

  const formatTime = (str) => {
    const [h, m] = str.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hr = h % 12 === 0 ? 12 : h % 12;
    return `${hr}:${String(m).padStart(2, "0")} ${ampm}`;
  };

  // 🔄 AUTO REFRESH FUNCTION — USED AFTER SAVE / UPDATE / DELETE
  const refreshCourts = async () => {
    if (!selectedVenue) return;
    try {
      const res = await fetch(
        `https://api.getfitindia.in/api/slots/${selectedVenue}/courts`,
        { headers: { ...getAuthHeader() } }
      );
      const data = await res.json();
      setCourts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to refresh courts");
    }
  };

  // ------------------- LOAD VENUES -------------------
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("https://api.getfitindia.in/api/venues", {
          headers: { ...getAuthHeader() },
        });
        const data = await res.json();
        setVenues(Array.isArray(data) ? data : []);
      } catch (err) {
        alert("Failed to load venues");
      }
    })();
  }, []);

  // ------------------- LOAD COURTS WHEN VENUE CHANGES -------------------
  useEffect(() => {
    if (!selectedVenue) {
      setVenueSports([]);
      setCourts([]);
      return;
    }

    const venue = venues.find((v) => v._id === selectedVenue);
    setVenueSports(venue?.sports || []);

    refreshCourts(); // 🔄 AUTO REFRESH WHEN VENUE CHANGES
  }, [selectedVenue, venues]);

  // ------------------- SLOT HANDLERS -------------------
  const addSlot = () => {
    setSlots((prev) => [...prev, { startTime: "", endTime: "", price: defaultPrice }]);
  };

  const resetForm = () => {
    setCourtName("");
    setSelectedSports([]);
    setSlots([{ startTime: "", endTime: "", price: defaultPrice }]);
    setEditCourtId(null);
  };

  // ------------------ PERIOD SLOT GENERATOR ------------------
  const generateSlots = (period) => {
    let start = "05:00";
    let end = "12:30";

    if (period === "afternoon") {
      start = "12:30";
      end = "16:30";
    } else if (period === "evening") {
      start = "16:30";
      end = "23:59";
    }

    const generated = [];
    let [h, m] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);

    while (h < eh || (h === eh && m < em)) {
      const s = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      m += 30;
      if (m >= 60) {
        h++;
        m -= 60;
      }
      const e = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      generated.push({ startTime: s, endTime: e, price: defaultPrice });
    }

    setSlots((prev) => {
      const combined = [...prev, ...generated];
      const unique = combined.filter(
        (slot, i, arr) =>
          i === arr.findIndex((s) => s.startTime === slot.startTime && s.endTime === slot.endTime)
      );
      return unique;
    });
  };

  // ------------------- DELETE COURT -------------------
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this court?")) return;

    try {
      await fetch(
        `https://api.getfitindia.in/api/slots/${selectedVenue}/courts/${id}`,
        {
          method: "DELETE",
          headers: { ...getAuthHeader() },
        }
      );

      await refreshCourts(); // 🔄 AUTO REFRESH AFTER DELETE

    } catch {
      alert("Delete failed");
    }
  };

  // ------------------- EDIT COURT -------------------
  const handleEdit = (court) => {
    setCourtName(court.courtName);
    setSelectedSports(court.sports || []);
    setSlots(court.slots || []);
    setEditCourtId(court._id);
  };

  // ------------------- SAVE COURT -------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedVenue) return alert("Select a venue first");
    if (!courtName.trim()) return alert("Enter court name");
    if (selectedSports.length === 0) return alert("Select at least 1 sport");

    const cleanedSlots = sanitizeSlots(slots);
    if (!cleanedSlots.length) return alert("Add at least one slot");
    if (!validateNoOverlap(cleanedSlots)) return alert("Slots overlap");

    const body = {
      courtName: courtName.trim(),
      sports: selectedSports,
      slots: cleanedSlots,
    };

    const method = editCourtId ? "PUT" : "POST";
    const url =
      editCourtId
        ? `https://api.getfitindia.in/api/slots/${selectedVenue}/courts/${editCourtId}`
        : `https://api.getfitindia.in/api/slots/${selectedVenue}/courts`;

    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json", ...getAuthHeader() },
      body: JSON.stringify(body),
    });

    if (!res.ok) return alert("Error saving");

    alert(editCourtId ? "Updated successfully!" : "Saved successfully!");

    resetForm();

    await refreshCourts(); // 🔄 AUTO REFRESH AFTER SAVE / UPDATE
  };

  // ======================================================================
  // UI SECTION (CourtOwner Style)
  // ======================================================================

  return (
    <div className="min-h-screen bg-gray-50 ">

      {/* HEADER */}
      <div className="bg-[#0f2250] text-white sticky top-0 z-20">
        <div className="max-w-6xl  px-4 py-4">
       <h1 className="text-lg font-semibold ms-12 sm:ms-0 mt-1">
  Manage Slots 
</h1>

        </div>
      </div>

      {/* MAIN CONTENT */}
   <div className="max-w-6xl mx-0 lg:mx-8 px-4 py-4">

        <div className="mt-3  md:p-0">

          {/* SELECT VENUE */}
          <label className="text-gray-700 text-sm font-medium mb-1">Select Venue</label>
          <select
            className="h-10 w-full rounded-lg border px-3 text-sm mb-4"
            value={selectedVenue}
            onChange={(e) => setSelectedVenue(e.target.value)}
          >
            <option value="">Select Venue</option>
            {venues.map((v) => (
              <option key={v._id} value={v._id}>
                {v.name}
              </option>
            ))}
          </select>

          {/* COURT NAME */}
          <label className="text-gray-700 text-sm font-medium mb-1">Court Name</label>
          <input
            className="h-10 w-full rounded-lg border px-3 text-sm mb-4"
            value={courtName}
            onChange={(e) => setCourtName(e.target.value)}
            placeholder="e.g. Court 1"
          />

          {/* SPORTS */}
          <label className="text-gray-700 text-sm font-medium mb-1">Select Sports</label>
          <div className="flex flex-wrap gap-3 mb-4">
            {venueSports.map((sport) => (
              <label key={sport} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selectedSports.includes(sport)}
                  onChange={() =>
                    setSelectedSports((prev) =>
                      prev.includes(sport)
                        ? prev.filter((p) => p !== sport)
                        : [...prev, sport]
                    )
                  }
                />
                {sport}
              </label>
            ))}
          </div>

          {/* DEFAULT PRICE */}
          <label className="text-gray-700 text-sm font-medium mb-1">Default Price</label>
          <div className="flex items-center h-10 rounded-lg border px-3 bg-gray-50 mb-4">
            <span className="mr-2">₹</span>
            <input
              className="w-full bg-transparent outline-none"
              value={defaultPrice}
              onChange={(e) => setDefaultPrice(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="500"
            />
          </div>

          {/* SLOT ROWS */}
          <label className="text-gray-700 text-sm font-medium mb-1">Slots</label>

          {slots.map((slot, idx) => (
            <div key={idx} className="grid grid-cols-3 gap-3 mb-3">
              <input
                type="time"
                className="h-10 rounded-lg border px-3 text-sm"
                value={slot.startTime}
                onChange={(e) =>
                  setSlots((prev) => {
                    const n = [...prev];
                    n[idx].startTime = e.target.value;
                    return n;
                  })
                }
              />
              <input
                type="time"
                className="h-10 rounded-lg border px-3 text-sm"
                value={slot.endTime}
                onChange={(e) =>
                  setSlots((prev) => {
                    const n = [...prev];
                    n[idx].endTime = e.target.value;
                    return n;
                  })
                }
              />
              <input
                className="h-10 rounded-lg border px-3 text-sm"
                value={slot.price}
                onChange={(e) =>
                  setSlots((prev) => {
                    const n = [...prev];
                    n[idx].price = e.target.value.replace(/[^\d]/g, "");
                    return n;
                  })
                }
                placeholder="₹ Price"
              />
            </div>
          ))}

          {/* ADD SLOT */}
          <button onClick={addSlot} className="text-blue-700 underline mb-6">
            + Add slot
          </button>

          {/* PERIOD BUTTONS */}
          <div className="mt-2 mb-6 flex flex-wrap gap-3">
            <button
              onClick={() => generateSlots("morning")}
              className="bg-blue-200 text-[#0f2250] px-4 h-9 rounded-lg text-sm font-medium"
            >
              Morning
            </button>
            <button
              onClick={() => generateSlots("afternoon")}
              className="bg-blue-200 text-[#0f2250] px-4 h-9 rounded-lg text-sm font-medium"
            >
              Afternoon
            </button>
            <button
              onClick={() => generateSlots("evening")}
              className="bg-blue-200 text-[#0f2250] px-4 h-9 rounded-lg text-sm font-medium"
            >
              Evening
            </button>
          </div>

          {/* SUBMIT BUTTON */}
          <button
            onClick={handleSubmit}
            className="w-full h-10 rounded-lg bg-[#0f2250] text-white font-semibold"
          >
            {editCourtId ? "Update Court" : "Save Court"}
          </button>

          {/* EXISTING COURTS */}
          <h2 className="mt-10 text-lg font-semibold text-gray-800">Existing Courts</h2>

          {courts.length === 0 && (
            <p className="text-gray-500 mt-2">No courts available.</p>
          )}

          <div className="mt-4 space-y-4">
            {courts.map((court) => (
              <div
                key={court._id}
                className="bg-white rounded-xl shadow-sm p-4 border"
              >
                <div className="flex justify-end gap-2 mb-2">
                  <button
                    onClick={() => handleEdit(court)}
                    className="p-2 rounded-full bg-yellow-400 hover:bg-yellow-500 text-white"
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(court._id)}
                    className="p-2 rounded-full bg-red-500 hover:bg-red-600 text-white"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                <h3 className="font-semibold text-gray-800">{court.courtName}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Sports: {court.sports.join(", ")}
                </p>

                <div className="grid md:grid-cols-3 gap-3 mt-4">
                  {court.slots.map((s, idx) => (
                    <div
                      key={idx}
                      className="bg-gray-100 rounded-lg p-3 border"
                    >
                      <p className="font-medium text-sm">
                        {formatTime(s.startTime)} – {formatTime(s.endTime)}
                      </p>
                      <p className="text-sm text-gray-600">₹ {s.price}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}

export default ManageSlots;
